import { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper'
import { ChildsSlots, RateSlotsAndSlots, StaffSlots } from '@/types/StaffRate.ts'
import { OccasionalSlot } from '@/types/Slots'
import { Daycare } from '@/types/Daycare.ts'
import { Work } from '@/types/work.ts'
import Title from '@/components/_custom/Title/title.tsx'
import DraftingStaffSlots from '../DraftingStaffSlots'
import DraftingChildSlots from '../DraftingChildSlots'
import DraftingAvailableSlots from '../DraftingAvailableSlots'
import draftingStyles from '../draftingHandler.module.scss'
import styles from './diagramExportPage.module.scss'

export interface SectionDiagramData {
    sectionId: number
    sectionName: string
    data: RateSlotsAndSlots
}

interface DiagramExportPageProps {
    date: string
    daycare: Daycare
    section: SectionDiagramData
    includeStaffs: boolean
    includeChilds: boolean
    includeAvailableSlots: boolean
}

const DiagramExportPage: FunctionComponent<DiagramExportPageProps> = ({
    date,
    daycare,
    section,
    includeStaffs,
    includeChilds,
    includeAvailableSlots
}: DiagramExportPageProps) => {
    const { t } = useTranslation()

    const { sectionName, data } = section

    const opening: string = data.rate.length > 0 ? data.rate[0].start : ''
    const closing: string = data.rate.length > 0 ? data.rate[data.rate.length - 1].end : ''
    const allWorks: Work[] = data.staffs.flatMap((staffSlots: StaffSlots): Work[] => staffSlots.works)
    return (
        <div className={styles.Page}>
            <div className={styles.PageHeader}>
                <p className={styles.PageHeaderDate}>
                    {format(parse(date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                        locale: getLocalFromLanguage()
                    })}
                </p>
                <p className={styles.PageHeaderSection}>{sectionName}</p>
                <p className={styles.PageHeaderDaycare}>{daycare.name}</p>
            </div>
            {includeStaffs && data.rate.length > 0 && (
                <div className={draftingStyles.DraftingContainer}>
                    <div className={draftingStyles.Header}>
                        <Title text={t('staffs')} />
                    </div>
                    {data.staffs.length > 0 ? (
                        data.staffs.map((staffSlots: StaffSlots) => (
                            <DraftingStaffSlots
                                key={staffSlots.staffId}
                                staffSlots={staffSlots}
                                opening={opening}
                                closing={closing}
                                date={date}
                                daycare={daycare}
                                staffsListData={[]}
                                allWorks={allWorks}
                                canWriteStaffWorks={false}
                                refetchData={() => {}}
                                setSelectedEvent={() => {}}
                                setShowSlotDialogOpen={() => {}}
                            />
                        ))
                    ) : (
                        <div className={draftingStyles.Text}>
                            <p>{t('noStaffThisDay')}</p>
                        </div>
                    )}
                </div>
            )}
            {includeChilds && data.rate.length > 0 && (
                <div className={draftingStyles.DraftingContainer}>
                    <div className={draftingStyles.Header}>
                        <Title text={t('childsPresent')} />
                    </div>
                    {data.childs.length > 0 ? (
                        data.childs.map((childSlots: ChildsSlots) => (
                            <DraftingChildSlots
                                key={childSlots.childId}
                                childSlots={childSlots}
                                opening={opening}
                                closing={closing}
                                setSelectedEvent={() => {}}
                                setShowSlotDialogOpen={() => {}}
                                date={date}
                            />
                        ))
                    ) : (
                        <div className={draftingStyles.Text}>
                            <p>{t('noChildThisDay')}</p>
                        </div>
                    )}
                </div>
            )}
            {includeAvailableSlots && data.rate.length > 0 && (
                <div className={draftingStyles.DraftingContainer}>
                    <div className={draftingStyles.Header}>
                        <Title text={t('availableSlots')} />
                    </div>
                    {data.availableSlots.length > 0 ? (
                        data.availableSlots.map((availableSlot: OccasionalSlot) => (
                            <DraftingAvailableSlots
                                key={availableSlot.id}
                                availableSlot={availableSlot}
                                opening={opening}
                                closing={closing}
                                setSelectedEvent={() => {}}
                                setShowSlotDialogOpen={() => {}}
                            />
                        ))
                    ) : (
                        <div className={draftingStyles.Text}>
                            <p>{t('noAvailableSlotsThisDay')}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default DiagramExportPage
